import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
    LayoutDashboard, Users, FileText, ShieldCheck, CreditCard,
    Layers, Database, Settings, History, Cookie, LogOut, Menu, ChevronRight,
    TrendingUp, ListChecks, Crown, Bell, UserCog, Landmark, PackageCheck, Ban, UsersRound, GalleryHorizontal
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { BRAND_LOGO } from '@/lib/logos';
import { InstallAppButton } from '@/components/InstallAppButton';

const NAV_GROUPS = [
    {
        label: 'Overview',
        items: [
            { to: '/admin', icon: TrendingUp, label: 'Sales Overview', testid: 'admin-sidebar-nav-overview' },
        ]
    },
    {
        label: 'Operations',
        items: [
            { to: '/admin/users', icon: Users, label: 'Users', testid: 'admin-sidebar-nav-users' },
            { to: '/admin/banners', icon: GalleryHorizontal, label: 'Host Banners', testid: 'admin-sidebar-nav-banners' },
            { to: '/admin/multi-accounts', icon: UsersRound, label: 'Multi-account Review', testid: 'admin-sidebar-nav-multi-accounts' },
            { to: '/admin/resellers', icon: Layers, label: 'Resellers', testid: 'admin-sidebar-nav-resellers' },
        ]
    },
    {
        label: 'Reseller Network',
        items: [
            { to: '/admin/noble-packs', icon: Crown, label: 'Noble Packs', testid: 'admin-sidebar-nav-noble' },
        ]
    },
    {
        label: 'Commerce',
        items: [
            { to: '/admin/plans', icon: CreditCard, label: 'Recharge Packages', testid: 'admin-sidebar-nav-plans' },
            { to: '/admin/payment-gateways', icon: Landmark, label: 'Payment Gateways', testid: 'admin-sidebar-nav-gateways' },
            { to: '/admin/stock-orders', icon: PackageCheck, label: 'Stock Orders', testid: 'admin-sidebar-nav-stock-orders' },
            { to: '/admin/user-stock-orders', icon: PackageCheck, label: 'User Stock Orders', testid: 'admin-sidebar-nav-user-stock-orders' },
            { to: '/admin/payments', icon: FileText, label: 'Payments', testid: 'admin-sidebar-nav-payments' },
            { to: '/admin/recharge-logs', icon: History, label: 'Recharge Logs', testid: 'admin-sidebar-nav-logs' },
            { to: '/admin/blocklist', icon: Ban, label: 'Blocklist', testid: 'admin-sidebar-nav-blocklist' },
        ]
    },
    {
        label: 'Bridge',
        items: [
            { to: '/admin/platforms', icon: Cookie, label: 'Platform Cookies', testid: 'admin-sidebar-nav-platforms' },
            { to: '/admin/session', icon: Cookie, label: 'Reseller Session', testid: 'admin-sidebar-nav-session' },
        ]
    },
    {
        label: 'Settings',
        items: [
            { to: '/admin/settings', icon: Settings, label: 'App Settings', testid: 'admin-sidebar-nav-settings' },
            { to: '/admin/policies', icon: Database, label: 'Policies', testid: 'admin-sidebar-nav-policies' },
            { to: '/admin/alerts', icon: Bell, label: 'Alerts', testid: 'admin-sidebar-nav-alerts' },
            { to: '/admin/otp-config', icon: ShieldCheck, label: 'OTP / Verification', testid: 'admin-sidebar-nav-otp' },
            { to: '/admin/account', icon: UserCog, label: 'Account', testid: 'admin-sidebar-nav-account' },
        ]
    },
];

function SidebarContent({ onNavigate }) {
    const location = useLocation();
    const { admin, logout } = useAuth();
    return (
        <div className="h-full flex flex-col bg-white">
            <div className="px-5 py-5 border-b border-border">
                <Link to="/admin" className="flex items-center gap-2">
                    <img src={BRAND_LOGO} alt="Eagle Eyes" className="w-9 h-9 rounded-lg object-cover ring-1 ring-border" />
                    <div>
                        <div className="font-display font-semibold text-foreground">Eagle Eyes</div>
                        <div className="text-[11px] text-muted-foreground -mt-0.5">Admin Console</div>
                    </div>
                </Link>
            </div>
            <ScrollArea className="flex-1 py-3">
                <div className="space-y-5 px-3" data-testid="admin-sidebar">
                    {NAV_GROUPS.map((group) => (
                        <div key={group.label}>
                            <div className="px-3 mb-1 text-[10px] uppercase tracking-wider text-muted-foreground/80 font-medium">{group.label}</div>
                            <div className="space-y-0.5">
                                {group.items.map((item) => {
                                    const Icon = item.icon;
                                    const active = location.pathname === item.to || (item.to !== '/admin' && location.pathname.startsWith(item.to));
                                    return (
                                        <Link
                                            key={item.to}
                                            to={item.to}
                                            data-testid={item.testid}
                                            onClick={onNavigate}
                                            className={`relative flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${active ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'}`}
                                        >
                                            {active && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-r bg-[#1F9D8A]" />}
                                            <Icon className="h-4 w-4" />
                                            <span>{item.label}</span>
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </ScrollArea>
            <div className="border-t border-border p-4">
                <div className="text-xs text-muted-foreground mb-2">{admin?.email || 'admin'}</div>
                <Button variant="outline" className="w-full justify-start gap-2" onClick={logout} data-testid="admin-logout-button">
                    <LogOut className="h-4 w-4" /> Logout
                </Button>
            </div>
        </div>
    );
}

export function AdminShell({ title, actions, children }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="min-h-screen bg-[#F7F8FA]">
            {/* Desktop sidebar */}
            <aside className="hidden lg:flex flex-col fixed top-0 left-0 h-screen w-72 border-r border-border bg-white">
                <SidebarContent />
            </aside>
            {/* Content */}
            <div className="lg:pl-72">
                <header className="sticky top-0 z-30 bg-white border-b border-border">
                    <div className="admin-header-bg">
                        <div className="px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Sheet open={open} onOpenChange={setOpen}>
                                    <SheetTrigger asChild>
                                        <Button variant="ghost" size="icon" className="lg:hidden" data-testid="admin-sidebar-toggle"><Menu className="h-5 w-5" /></Button>
                                    </SheetTrigger>
                                    <SheetContent side="left" className="p-0 w-72">
                                        <SidebarContent onNavigate={() => setOpen(false)} />
                                    </SheetContent>
                                </Sheet>
                                <h1 className="font-display text-lg sm:text-xl font-semibold text-foreground" data-testid="admin-page-title">{title}</h1>
                            </div>
                            <div className="flex items-center gap-2"><InstallAppButton testid="admin-install-app-button" variant="default" className="bg-[#0E2A47] hover:bg-[#0B223A] text-white border-0" />{actions}</div>
                        </div>
                    </div>
                </header>
                <main className="px-4 sm:px-6 lg:px-8 py-6">{children}</main>
            </div>
        </div>
    );
}
